import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private logLevel: LogLevel = environment.production ? LogLevel.WARN : LogLevel.DEBUG;
  private context?: string;

  setContext(context: string): LoggerService {
    const newLogger = Object.create(this) as LoggerService;
    newLogger.context = context;
    newLogger.logLevel = this.logLevel;
    return newLogger;
  }

  debug(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.DEBUG, message, optionalParams);
  }

  info(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.INFO, message, optionalParams);
  }

  warn(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.WARN, message, optionalParams);
  }

  error(message: string, ...optionalParams: any[]): void {
    this.log(LogLevel.ERROR, message, optionalParams);
  }

  private log(level: LogLevel, message: string, params: any[]): void {
    if (level < this.logLevel) {
      return;
    }

    const timestamp = this.getSimpleTimestamp();
    const prefix = this.context ? `[${this.context}]` : '';
    const formattedMessage = `[${timestamp}] ${prefix} ${message}`;

    switch (level) {
      case LogLevel.DEBUG:
        console.log(`🔍 DEBUG: ${formattedMessage}`, ...params);
        break;
      case LogLevel.INFO:
        console.log(`ℹ️ INFO: ${formattedMessage}`, ...params);
        break;
      case LogLevel.WARN:
        console.warn(`⚠️ WARN: ${formattedMessage}`, ...params);
        break;
      case LogLevel.ERROR:
        console.error(`❌ ERROR: ${formattedMessage}`, ...params);
        break;
    }
  }

  private getSimpleTimestamp(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const milliseconds = now.getMilliseconds().toString().padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  }
}
